/* ============================================================================
   RootErrorBoundary
   ----------------------------------------------------------------------------
   Catches render errors from anywhere in the React tree and shows a visible
   debug card on screen instead of a silent black screen. Without this, any
   uncaught throw at the top of the tree (provider, Router, etc.) would unmount
   everything and the user would just see the body's dark green gradient with
   no clue what went wrong.

   We deliberately keep this dependency-free (no Tailwind, no shadcn) so it
   still renders even if a CSS layer or design token failed to load.
   ============================================================================ */
import { Component, ErrorInfo, ReactNode } from "react";
import { Sentry } from "@/lib/sentry";
import { handleStaleChunkError, willHandleStaleChunkError } from "@/lib/staleChunkReload";
import { BUILD_ID } from "@/lib/buildId";

interface Props {
  children: ReactNode;
}

interface State {
  err: Error | null;
  info: ErrorInfo | null;
  /** A stale-chunk reload is under way, so render() stays quiet. */
  reloading: boolean;
}

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { err: null, info: null, reloading: false };

  static getDerivedStateFromError(err: Error): Partial<State> {
    // Runs in the render phase, so it may only read — the reload itself is a
    // side effect and stays in componentDidCatch below. But it is also the
    // only place that can stop the card from painting at all: what render()
    // shows is decided from the state set here, and componentDidCatch runs
    // after the commit, too late to prevent that first paint. So a chunk that
    // a deploy deleted, and that we are about to reload away, still flashed a
    // red crash card for the fraction of a second before the document was
    // replaced — describing nothing the user could act on.
    //
    // `err` is still recorded either way, so the children stay unmounted and
    // cannot throw again on a retry render. Only the card is suppressed.
    return { err, reloading: willHandleStaleChunkError(err) };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // A chunk deleted by a deploy is not a crash: this tab is holding an old
    // index.html and needs the current one. Reload instead of showing a card
    // and filing an error — neither describes anything anyone can act on, and
    // in production every one of these was an admin page left open across a
    // deploy. Returns false if we already tried, in which case reloading is
    // evidently not the fix and the error should surface normally below.
    if (handleStaleChunkError(err)) return;

    // No reload after all — the guard declined, so reloading is evidently not
    // the fix. Clear the quiet flag getDerivedStateFromError may have set so
    // the card shows and the error is reported below.
    this.setState({ info, reloading: false });
    // Workstream C: forward render crashes to Sentry when configured. No-op
    // when DSN is absent, so this stays safe for non-production builds.
    try {
      Sentry.captureException(err, {
        contexts: { react: { componentStack: info.componentStack } },
        tags: { boundary: "root" },
      });
    } catch {
      /* never throw from a boundary */
    }
    // Best-effort log to /api/errors so admin can see TestFlight crashes
    // without needing Xcode device logs.
    try {
      const apiBase =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__NEXUS_DEX_API_BASE__ ?? "";
      const url = apiBase ? `${apiBase}/api/errors` : "/api/errors";
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: `[boundary] ${err.message}`,
          stack: err.stack,
          route: window.location.hash || window.location.pathname,
          level: "error",
          buildId: BUILD_ID,
        }),
      }).catch(() => {});
    } catch {
      /* ignore */
    }
    // Also log to console so Xcode device logs / Safari Web Inspector pick
    // it up.
    // eslint-disable-next-line no-console
    console.error("[RootErrorBoundary]", err, info);
  }

  render(): ReactNode {
    // A reload is already under way; the card would only flash before the
    // document is replaced. Render nothing and let the body gradient show.
    if (this.state.reloading) return null;
    if (this.state.err) {
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999999,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
            gap: 12,
            padding: "max(var(--nx-safe-top), 24px) 20px 20px",
            background: "#0b0d12",
            color: "#fafafa",
            fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
            fontSize: 13,
            lineHeight: 1.5,
            overflow: "auto",
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#8b8f99" }}>
            Nexus Dex - render error
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#ff6b6b" }}>
            {this.state.err.name}: {this.state.err.message}
          </div>
          {this.state.err.stack && (
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 11,
                background: "#15171c",
                border: "1px solid #2a2d35",
                borderRadius: 8,
                padding: 12,
                color: "#cfd2d8",
                maxHeight: "40vh",
                overflow: "auto",
              }}
            >
              {this.state.err.stack}
            </pre>
          )}
          {this.state.info?.componentStack && (
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 11,
                background: "#15171c",
                border: "1px solid #2a2d35",
                borderRadius: 8,
                padding: 12,
                color: "#9aa0ad",
                maxHeight: "30vh",
                overflow: "auto",
              }}
            >
              {this.state.info.componentStack}
            </pre>
          )}
          <button
            type="button"
            onClick={() => {
              this.setState({ err: null, info: null, reloading: false });
              try {
                window.location.hash = "#/";
                window.location.reload();
              } catch {
                /* noop */
              }
            }}
            style={{
              marginTop: 8,
              padding: "12px 16px",
              background: "#1f6b4a",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Reload app
          </button>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>
            If this keeps happening, screenshot this screen and send it back.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
